import { GetServerSideProps } from "next";
import Head from "next/head";
import Image from "next/image";
import EventListing from "../../../../components/event-list/EventListing";
import config from "../../../../config";
import {
    DepartmentVisibility,
    EcommerceEvent,
    EcommerceEventList,
    EventListType,
} from "../../../../models/Event";
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Grid,
    IconButton,
    Typography,
} from "@mui/material";
import CartIcon from "@mui/icons-material/ShoppingCart";
import {
    displayDateRange,
    getImageUrl,
    setupStyleMapFromStyleMap,
} from "../../../../utils/helpers";
import React from "react";
import ThemeStore from "../../../../stores/ThemeStore";
import PageWrapper from "../../../../components/PageWrapper";
import DepartmentListing from "../../../../components/event-list/DepartmentListing";
import Link from "next/link";
import Cart from "../../../../components/cart/Cart";
import CartStore, { useCartStore } from "../../../../stores/CartStore";
import SleepIcon from "../../../../icons/SleepIcon";
import NothingHereYet from "../../../../components/NothingHereYet";
import { observer } from "mobx-react-lite";
import AuthStore, { COOKIE_BOXOFFICE_KEY } from "../../../../stores/AuthStore";
import dynamic from "next/dynamic";
import Divider from "@mui/material/Divider";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { gtm } from "../../../../utils/gtm";
import { tokenIsValid } from "../../../../utils/jwtHelpers";
import { useRouter } from "next/router";
import CustomEventList from "../../../../components/event-list/CustomEventList";
import ScheduleIcon from "@mui/icons-material/Schedule";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EventIcon from "@mui/icons-material/Event";
import { Chip } from "@mui/material";

const DepartmentList = dynamic(
    () => import("../../../../components/event-list/DepartmentList"),
    { ssr: false },
);

const Calendar = dynamic(
    () => import("../../../../components/calendar/Calendar"),
    { ssr: false },
);

const Home = (props: {
    eventList: EcommerceEvent;
    style: EcommerceEventList;
    errorMessage: string | null;
}) => {
    const [showEventDetails, setShowEventDetails] = React.useState(false);

    const venue = props.eventList.event?.venueConfiguration?.venue;
    const cartStore = useCartStore(props.eventList.cartUrl);

    function toggleEventDetails() {
        setShowEventDetails(!showEventDetails);
    }

    React.useEffect(() => {
        if (props.eventList.newStyleMap) {
            setupStyleMapFromStyleMap(props.eventList.newStyleMap);
        }
    }, []);

    const pageTitle = `${
        props.eventList.event?.title ?? props.eventList.clientName
    } Tickets | AuthentiGATE`;

    const eventListType =
        props.eventList.event?.eventList?.type ?? EventListType.DEFAULT;

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta
                    name="description"
                    content={`Buy tickets for ${
                        props.eventList.event?.title ??
                        props.eventList.clientName
                    }.`}
                />
            </Head>
            {eventListType === EventListType.DEFAULT && (
                <PageWrapper
                    eventList={props.style}
                    overrideLogo={props.eventList.event?.eventList?.style.logo}
                >
                    <Container maxWidth="xl">
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Link
                                    href={`/store/${props.eventList.cartUrl}`}
                                >
                                    <Button variant="contained">
                                        Back to Events
                                    </Button>
                                </Link>
                            </Grid>
                            {props.errorMessage ? (
                                <Grid item xs={12}>
                                    <NothingHereYet message="Event is not currently active." />
                                </Grid>
                            ) : (
                                <>
                                    <Grid
                                        item
                                        xs={12}
                                        md={8}
                                        order={{ xs: 2, md: 1 }}
                                        key={props.eventList.event?.departments
                                            .map((it) => it.id)
                                            .join("")}
                                    >
                                        <DepartmentList
                                            eventList={props.eventList}
                                            cartStore={cartStore}
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        md={4}
                                        order={{ xs: 1, md: 2 }}
                                        key={props.eventList.event?.id}
                                        sx={{
                                            position: { md: "sticky" },
                                            top: "20px",
                                            alignSelf: "flex-start",
                                        }}
                                    >
                                        <Card
                                            sx={{
                                                backgroundColor: (theme) =>
                                                    theme.palette.mode === "dark"
                                                        ? "rgba(30,30,30,0.95)"
                                                        : "rgba(255,255,255,0.95)",
                                                backdropFilter: "blur(10px)",
                                                border: (theme) =>
                                                    theme.palette.mode ===
                                                    "dark"
                                                        ? "1px solid rgba(255,255,255,0.1)"
                                                        : "1px solid rgba(0,0,0,0.05)",
                                                borderRadius: 3,
                                                boxShadow: (theme) =>
                                                    theme.palette.mode ===
                                                    "dark"
                                                        ? "0 8px 32px rgba(0,0,0,0.3)"
                                                        : "0 8px 32px rgba(0,0,0,0.1)",
                                                transition: "all 0.3s ease",
                                                "&:hover": {
                                                    transform:
                                                        "translateY(-2px)",
                                                    boxShadow: (theme) =>
                                                        theme.palette.mode ===
                                                        "dark"
                                                            ? "0 12px 40px rgba(0,0,0,0.4)"
                                                            : "0 12px 40px rgba(0,0,0,0.15)",
                                                },
                                            }}
                                        >
                                            <CardContent sx={{ p: 3 }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        mb: 2,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            backgroundColor: (
                                                                theme,
                                                            ) =>
                                                                theme.palette
                                                                    .primary
                                                                    .main,
                                                            borderRadius: "50%",
                                                            p: 1,
                                                            mr: 2,
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                        }}
                                                    >
                                                        <EventIcon
                                                            sx={{
                                                                fontSize: 20,
                                                                color: "white",
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography
                                                        variant="h4"
                                                        sx={{
                                                            fontWeight: "bold",
                                                            color: (theme) => theme.palette.primary.main,
                                                            lineHeight: 1.2,
                                                        }}
                                                    >
                                                        {
                                                            props.eventList
                                                                .event?.title
                                                        }
                                                    </Typography>
                                                </Box>
                                                {props.eventList.event
                                                    ?.showEventDates && (
                                                    <Box
                                                        sx={{
                                                            backgroundColor: (
                                                                theme,
                                                            ) =>
                                                                `${theme.palette.primary.main}20`,
                                                            borderRadius: 2,
                                                            p: 2,
                                                            mb: 2,
                                                            border: (theme) =>
                                                                `1px solid ${theme.palette.primary.main}40`,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: "text.primary",
                                                                textAlign:
                                                                    "center",
                                                            }}
                                                        >
                                                            {displayDateRange(
                                                                props.eventList
                                                                    .event
                                                                    .eventFrom,
                                                                props.eventList
                                                                    .event
                                                                    .eventTo,
                                                                true,
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {props.eventList.event
                                                    ?.details && (
                                                    <>
                                                        <Divider
                                                            sx={{ my: 2 }}
                                                        />
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent:
                                                                    "center",
                                                            }}
                                                        >
                                                            <Button
                                                                onClick={() => {
                                                                    toggleEventDetails();
                                                                }}
                                                                sx={{
                                                                    display: {
                                                                        xs: "flex",
                                                                        md: "none",
                                                                    },
                                                                    gap: 0.5,
                                                                    backgroundColor:
                                                                        (
                                                                            theme,
                                                                        ) =>
                                                                            theme
                                                                                .palette
                                                                                .primary
                                                                                .main,
                                                                    color: "white",
                                                                    "&:hover": {
                                                                        backgroundColor:
                                                                            (
                                                                                theme,
                                                                            ) =>
                                                                                theme
                                                                                    .palette
                                                                                    .primary
                                                                                    .dark,
                                                                    },
                                                                }}
                                                                variant="contained"
                                                                size="small"
                                                                fullWidth
                                                            >
                                                                {showEventDetails
                                                                    ? "Hide Event Details"
                                                                    : "Show Event Details"}
                                                                {showEventDetails ? (
                                                                    <ExpandLessIcon fontSize="small" />
                                                                ) : (
                                                                    <ExpandMoreIcon fontSize="small" />
                                                                )}
                                                            </Button>
                                                        </Box>
                                                        <Box
                                                            component="div"
                                                            sx={{
                                                                display: {
                                                                    xs: showEventDetails
                                                                        ? "block"
                                                                        : "none",
                                                                    md: "block",
                                                                },
                                                                mt: 2,
                                                                p: 2,
                                                                background: (
                                                                    theme,
                                                                ) =>
                                                                    theme
                                                                        .palette
                                                                        .mode ===
                                                                    "dark"
                                                                        ? "rgba(255,255,255,0.05)"
                                                                        : "rgba(0,0,0,0.02)",
                                                                borderRadius: 2,
                                                                border: (
                                                                    theme,
                                                                ) =>
                                                                    theme
                                                                        .palette
                                                                        .mode ===
                                                                    "dark"
                                                                        ? "1px solid rgba(255,255,255,0.1)"
                                                                        : "1px solid rgba(0,0,0,0.05)",
                                                            }}
                                                            dangerouslySetInnerHTML={{
                                                                __html:
                                                                    props
                                                                        .eventList
                                                                        .event
                                                                        ?.details ??
                                                                    "",
                                                            }}
                                                        />
                                                    </>
                                                )}
                                                <Divider sx={{ my: 2 }} />
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        mb: 2,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            backgroundColor: (
                                                                theme,
                                                            ) =>
                                                                theme.palette
                                                                    .primary
                                                                    .main,
                                                            borderRadius: "50%",
                                                            p: 1,
                                                            mr: 2,
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                        }}
                                                    >
                                                        <LocationOnIcon
                                                            sx={{
                                                                fontSize: 20,
                                                                color: "white",
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontWeight: "bold",
                                                            color: (theme) =>
                                                                theme.palette
                                                                    .primary
                                                                    .main,
                                                        }}
                                                    >
                                                        Venue
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        backgroundColor: (
                                                            theme,
                                                        ) =>
                                                            `${theme.palette.primary.main}20`,
                                                        borderRadius: 2,
                                                        p: 2,
                                                        border: (theme) =>
                                                            `1px solid ${theme.palette.primary.main}40`,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: "text.primary",
                                                            mb: 0.5,
                                                        }}
                                                    >
                                                        {venue?.name}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        {venue?.address},{" "}
                                                        {venue?.city},{" "}
                                                        {venue?.state}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        order={3}
                                        key={"checkout-button"}
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Link
                                            href={`/store/${props.eventList.cartUrl}/checkout`}
                                        >
                                            <Button variant="contained">
                                                Proceed to Checkout
                                            </Button>
                                        </Link>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Container>
                </PageWrapper>
            )}
            {eventListType === EventListType.DATE_SESSION_PICKER && (
                <PageWrapper
                    eventList={props.style}
                    overrideLogo={props.eventList.event?.eventList?.style?.logo}
                    background={
                        props.eventList.event?.eventList?.style.background
                    }
                >
                    <Container maxWidth="lg">
                        {props.errorMessage ? (
                            <NothingHereYet message="Event is not currently active." />
                        ) : (
                            <Calendar
                                style={props.eventList.event!.eventList!.style}
                                config={
                                    props.eventList.event?.eventList?.config!
                                }
                                departments={
                                    props.eventList.event?.departments ?? []
                                }
                                clientIds={
                                    props.eventList.event?.client
                                        .equivalentEMS2ClientIds ?? []
                                }
                            />
                        )}
                    </Container>
                </PageWrapper>
            )}
            {eventListType === EventListType.CUSTOM && cartStore && (
                <PageWrapper
                    eventList={props.style}
                    overrideLogo={props.eventList.event?.eventList?.style.logo}
                    background={
                        props.eventList.event?.eventList?.style.background
                    }
                >
                    <Container maxWidth="xl">
                        <Link href={`/store/${props.eventList.cartUrl}`}>
                            <Button variant="contained">Back to Events</Button>
                        </Link>
                        {props.errorMessage ? (
                            <NothingHereYet message="Event is not currently active." />
                        ) : (
                            <CustomEventList
                                eventList={props.eventList}
                                cartStore={cartStore}
                                template={
                                    props.eventList.event!.eventList!.config
                                        .customTemplate!
                                }
                            />
                        )}
                    </Container>
                </PageWrapper>
            )}
            {eventListType === EventListType.COMING_SOON && (
                <PageWrapper
                    eventList={props.style}
                    overrideLogo={props.eventList.event?.eventList?.style.logo}
                    background={
                        props.eventList.event?.eventList?.style.background
                    }
                >
                    <Container maxWidth="lg">
                        <Grid container spacing={4} justifyContent="center">
                            <Grid item xs={12}>
                                <Link
                                    href={`/store/${props.eventList.cartUrl}`}
                                >
                                    <Button variant="contained">
                                        Back to Events
                                    </Button>
                                </Link>
                            </Grid>
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        textAlign: "center",
                                        py: 6,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            backgroundColor: (theme) =>
                                                theme.palette.primary.main,
                                            borderRadius: "50%",
                                            width: 120,
                                            height: 120,
                                            mb: 3,
                                            boxShadow: (theme) =>
                                                `0 10px 30px ${theme.palette.primary.main}30`,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <ScheduleIcon
                                            sx={{
                                                fontSize: 60,
                                                color: "white",
                                            }}
                                        />
                                    </Box>
                                    <Typography
                                        variant="h2"
                                        sx={{
                                            fontWeight: "bold",
                                            mb: 2,
                                            color: (theme) =>
                                                theme.palette.primary.main,
                                        }}
                                    >
                                        Coming Soon
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        color="text.secondary"
                                        sx={{ mb: 4, maxWidth: 600 }}
                                    >
                                        {props.eventList.event?.title} tickets
                                        will be available soon. Stay tuned for
                                        updates!
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>
                </PageWrapper>
            )}
            <Cart />
        </>
    );
};

export default Home;

export const getServerSideProps: GetServerSideProps = async (context) => {
    const cartUrl = context.params?.cartUrl,
        eventId = context.params?.eventId;
    let data: any,
        style: any,
        error = false,
        errorMessage: string | null = null;

    const boxoffice = context.req.cookies[COOKIE_BOXOFFICE_KEY];
    let salesContext = "ECOMMERCE";

    if (
        boxoffice == "true" &&
        tokenIsValid(
            context.req.cookies[
                config.COOKIE_PREFIX + "__authentigate_token_sanitized"
            ],
        )
    ) {
        salesContext = "BOXOFFICE";
    }

    const startTime = new Date().getTime();

    try {
        const res = await fetch(
            `${config.BACKEND_URI}/sales/ecommerce-events/${cartUrl}/${eventId}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    "X-Sales-Context": salesContext,
                    Cookie: context.req.headers.cookie ?? "",
                },
                credentials: "include",
            },
        );
        if (res.status !== 200) {
            error = true;
            console.log(await res.text());
        } else {
            data = await res.json();
            if (!data.event || !data.event.isActive) {
                errorMessage = "Event is not currently active.";
            }
        }
    } catch (err) {
        console.log(err);
        error = true;
    }

    const endTime = new Date().getTime();
    const timeDiff = endTime - startTime;
    console.log(`Fetch time: ${timeDiff}ms`);

    try {
        const res = await fetch(
            `${config.BACKEND_URI}/sales/cart-style/${cartUrl}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    "X-Sales-Context": "ECOMMERCE",
                },
            },
        );
        if (res.status !== 200) {
            error = true;
            console.log(await res.text());
        } else {
            style = await res.json();
        }
    } catch (err) {
        console.log(err);
        error = true;
    }

    if (data?.newStyleMap) {
        setupStyleMapFromStyleMap(data.newStyleMap);
    }

    return {
        props: { eventList: data, style, errorMessage },
        notFound: error,
    };
};
